import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import type { Insured, Policy } from "@shared/schema";
import { IN_FORCE_STATUSES } from "@shared/schema";
import { StatCard, type StatTone } from "./StatCard";
import {
  aggregateVehicles,
  aggregateTrailers,
  aggregateDrivers,
  type VehicleEntry,
  type DriverEntry,
} from "./assets/asset-helpers";

interface Props {
  insured: Insured;
  policies: Policy[];
}

interface StatEntry {
  label: string;
  value: string | number | null;
  tone: StatTone;
}

// CSR-664: Counts policies whose status is in IN_FORCE_STATUSES, matching the
// "Active Policies" column on the Insureds list. The previous implementation
// also required the policy's effective/expiration window to bracket today,
// which excluded recently bound or recently lapsed in-force policies whose
// status hadn't yet been advanced upstream. Status-only here keeps the
// overview "Policies" stat in lock-step with the list page.
export function countInForce(policies: Policy[]): number {
  return policies.filter((p) =>
    IN_FORCE_STATUSES.includes(
      (p.status?.toLowerCase() ?? "") as (typeof IN_FORCE_STATUSES)[number]
    )
  ).length;
}

// CSR-1968: Quick Stats counts the insured's actual active assets — the same
// deduped, lifecycle-aware aggregation the Assets tab renders — restricted to
// active (in-force, not removed) entries. Previously Power Units / Drivers read
// insured.totalPowerUnits / totalDrivers, which are the FMCSA-census fleet size
// (self-reported to FMCSA), not the vehicles/drivers on the insured's policies,
// so the Overview and Assets tab could never agree.
function countActive(entries: (VehicleEntry | DriverEntry)[]): number {
  return entries.filter((e) => e.active).length;
}

export function QuickStatsGrid({ policies }: Props) {
  const stats = useMemo<StatEntry[]>(() => {
    return [
      { label: "Policies", value: countInForce(policies), tone: "default" },
      { label: "Power Units", value: countActive(aggregateVehicles(policies)), tone: "orange" },
      { label: "Trailers", value: countActive(aggregateTrailers(policies)), tone: "orange" },
      { label: "Drivers", value: countActive(aggregateDrivers(policies)), tone: "green" },
      { label: "Conversations", value: null, tone: "orange" },
    ];
  }, [policies]);

  return (
    <Card className="p-6 bg-card" data-testid="quick-stats-grid">
      <div className="mb-4">
        <h3 className="text-base font-semibold">Quick Stats</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {stats.map((s) => (
          <StatCard key={s.label} label={s.label} value={s.value} tone={s.tone} />
        ))}
      </div>
    </Card>
  );
}
